import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap,
  GitBranch,
  Database,
  FileText,
  Shield,
  Target,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Search,
  Filter,
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  AlertCircle,
  Layers,
  TrendingUp,
  Link2,
  Activity,
  BarChart3,
  Info
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = process.env.REACT_APP_BACKEND_URL;

// Node type configurations
const NODE_TYPES = {
  kpi: { 
    icon: Database, 
    color: 'blue', 
    bgClass: 'bg-blue-100', 
    textClass: 'text-blue-700', 
    borderClass: 'border-blue-500',
    label: 'KPI'
  },
  claim: { 
    icon: Shield, 
    color: 'green', 
    bgClass: 'bg-green-100', 
    textClass: 'text-green-700', 
    borderClass: 'border-green-500',
    label: 'Claim'
  },
  requirement: { 
    icon: Target, 
    color: 'purple', 
    bgClass: 'bg-purple-100', 
    textClass: 'text-purple-700', 
    borderClass: 'border-purple-500',
    label: 'Requirement'
  },
  report: { 
    icon: FileText, 
    color: 'amber', 
    bgClass: 'bg-amber-100', 
    textClass: 'text-amber-700', 
    borderClass: 'border-amber-500',
    label: 'Report'
  },
  evidence: { 
    icon: Link2, 
    color: 'slate', 
    bgClass: 'bg-slate-100', 
    textClass: 'text-slate-700', 
    borderClass: 'border-slate-500',
    label: 'Evidence'
  }
};

// Severity configurations
const SEVERITY_CONFIG = {
  critical: { color: 'red', bgClass: 'bg-red-100', textClass: 'text-red-700', icon: AlertTriangle },
  high: { color: 'orange', bgClass: 'bg-orange-100', textClass: 'text-orange-700', icon: AlertCircle },
  medium: { color: 'amber', bgClass: 'bg-amber-100', textClass: 'text-amber-700', icon: Info },
  low: { color: 'green', bgClass: 'bg-green-100', textClass: 'text-green-700', icon: CheckCircle2 }
};

const ImpactEngine = () => {
  const [kpis, setKpis] = useState([]);
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [impactAnalysis, setImpactAnalysis] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('analyze'); // 'analyze' or 'graph'
  const [searchTerm, setSearchTerm] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [expandedSections, setExpandedSections] = useState({
    requirements: true,
    claims: true,
    recommendations: true
  });

  // Fetch KPIs on mount
  useEffect(() => {
    const fetchKpis = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${API}/api/disclosure/kpis`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setKpis(data.kpis || []);
          }
        }
      } catch (err) {
        console.error('Error fetching KPIs:', err);
        setError('Failed to load KPIs');
      } finally {
        setLoading(false);
      }
    };
    
    fetchKpis();
  }, []);

  // Analyze KPI impact
  const analyzeKpiImpact = useCallback(async (kpiId) => {
    try {
      setAnalyzing(true);
      setImpactAnalysis(null);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/disclosure/impact/kpi/${kpiId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setImpactAnalysis(data);
        } else {
          setError(data.error || 'Failed to analyze impact');
        }
      }
    } catch (err) {
      console.error('Error analyzing KPI impact:', err);
      setError('Failed to analyze impact');
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // Fetch impact graph
  const fetchImpactGraph = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/api/disclosure/impact/graph`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGraphData(data);
        }
      }
    } catch (err) {
      console.error('Error fetching graph:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle KPI selection
  const handleKpiSelect = (kpi) => {
    setSelectedKpi(kpi);
    analyzeKpiImpact(kpi.id);
  };

  // Filter KPIs
  const filteredKpis = kpis.filter(kpi => {
    if (domainFilter !== 'all' && kpi.domain !== domainFilter) return false;
    if (searchTerm && !kpi.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !kpi.code?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Group KPIs by domain
  const groupedKpis = filteredKpis.reduce((acc, kpi) => {
    const domain = kpi.domain || 'Other';
    if (!acc[domain]) acc[domain] = [];
    acc[domain].push(kpi);
    return acc;
  }, {});

  const domainLabels = {
    'E': 'Environmental',
    'S': 'Social',
    'G': 'Governance'
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading && kpis.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="impact-loading">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
        <span className="ml-3 text-slate-600">Loading Impact Engine...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="impact-engine">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-700 rounded-xl p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Zap size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Impact Engine</h1>
              <p className="text-indigo-100">Analyze downstream effects of KPI and data changes</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white/10 rounded-lg p-1">
              <button
                onClick={() => setViewMode('analyze')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'analyze' ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                }`}
                data-testid="view-analyze-btn"
              >
                <Activity size={16} className="inline mr-2" />
                Analyze
              </button>
              <button
                onClick={() => { setViewMode('graph'); fetchImpactGraph(); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  viewMode === 'graph' ? 'bg-white text-indigo-700' : 'text-white hover:bg-white/10'
                }`}
                data-testid="view-graph-btn"
              >
                <GitBranch size={16} className="inline mr-2" />
                Graph View
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Explanation */}
      <div className="bg-slate-900 rounded-xl p-4 text-white">
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-400">Impact Flow:</span>
          {[
            { label: 'KPI Change', icon: Database, color: 'blue' },
            { label: 'Claims Affected', icon: Shield, color: 'green' },
            { label: 'Requirements Impacted', icon: Target, color: 'purple' },
            { label: 'Reports to Update', icon: FileText, color: 'amber' }
          ].map((step, idx) => (
            <React.Fragment key={step.label}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full bg-${step.color}-500/20 flex items-center justify-center`}>
                  <step.icon size={16} className={`text-${step.color}-400`} />
                </div>
                <span className="text-sm">{step.label}</span>
              </div>
              {idx < 3 && <ArrowRight size={16} className="text-slate-500" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {viewMode === 'analyze' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* KPI Selector Panel */}
          <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="kpi-selector">
            <div className="p-4 bg-slate-50 border-b border-slate-200">
              <h2 className="font-bold text-slate-900 flex items-center gap-2">
                <Database size={18} className="text-blue-600" />
                Select KPI to Analyze
              </h2>
              <p className="text-sm text-slate-500 mt-1">Choose a KPI to see its downstream impact</p>
            </div>
            
            {/* Search & Filter */}
            <div className="p-3 border-b border-slate-200 space-y-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search KPIs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm"
                  data-testid="kpi-search"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'E', 'S', 'G'].map(domain => (
                  <button
                    key={domain}
                    onClick={() => setDomainFilter(domain)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                      domainFilter === domain 
                        ? 'bg-indigo-100 text-indigo-700' 
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {domain === 'all' ? 'All' : domainLabels[domain] || domain}
                  </button>
                ))}
              </div>
            </div>
            
            {/* KPI List */}
            <div className="max-h-[500px] overflow-y-auto">
              {Object.entries(groupedKpis).map(([domain, domainKpis]) => (
                <div key={domain}>
                  <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                    <span className="text-xs font-semibold text-slate-500 uppercase">
                      {domainLabels[domain] || domain}
                    </span>
                  </div>
                  {domainKpis.map(kpi => (
                    <button
                      key={kpi.id}
                      onClick={() => handleKpiSelect(kpi)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-all ${
                        selectedKpi?.id === kpi.id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                      }`}
                      data-testid={`kpi-${kpi.code}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900 text-sm">{kpi.name}</p>
                          <p className="text-xs text-slate-500">{kpi.code} • {kpi.unit}</p>
                        </div>
                        <ChevronRight size={16} className="text-slate-400" />
                      </div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Impact Analysis Panel */}
          <div className="lg:col-span-2 space-y-4">
            {analyzing && (
              <div className="bg-white rounded-xl shadow p-12 text-center">
                <RefreshCw className="animate-spin text-indigo-600 mx-auto" size={32} />
                <p className="mt-3 text-slate-600">Analyzing impact...</p>
              </div>
            )}

            {!analyzing && !impactAnalysis && !selectedKpi && (
              <div className="bg-white rounded-xl shadow p-12 text-center" data-testid="no-selection">
                <Zap size={48} className="mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Select a KPI to Analyze</h3>
                <p className="text-slate-500">Choose a KPI from the left panel to see its downstream impact on claims, requirements, and reports.</p>
              </div>
            )}

            {impactAnalysis && impactAnalysis.success && (
              <>
                {/* Impact Summary */}
                <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="impact-summary">
                  <div className="p-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-slate-900">{impactAnalysis.kpi?.name}</h3>
                        <p className="text-sm text-slate-500">{impactAnalysis.kpi?.code} • {impactAnalysis.kpi?.unit}</p>
                      </div>
                      <div className={`px-4 py-2 rounded-lg ${SEVERITY_CONFIG[impactAnalysis.impact_summary?.severity]?.bgClass}`}>
                        <span className={`text-sm font-bold ${SEVERITY_CONFIG[impactAnalysis.impact_summary?.severity]?.textClass}`}>
                          {impactAnalysis.impact_summary?.severity?.toUpperCase()} IMPACT
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-4 divide-x divide-slate-200">
                    <div className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-900">{impactAnalysis.impact_summary?.total_affected || 0}</p>
                      <p className="text-xs text-slate-500">Total Affected</p>
                    </div>
                    <div className="p-4 text-center">
                      <p className="text-2xl font-bold text-purple-600">{impactAnalysis.impact_summary?.requirements_affected || 0}</p>
                      <p className="text-xs text-slate-500">Requirements</p>
                    </div>
                    <div className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{impactAnalysis.impact_summary?.claims_affected || 0}</p>
                      <p className="text-xs text-slate-500">Claims</p>
                    </div>
                    <div className="p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{impactAnalysis.impact_summary?.critical_claims_at_risk || 0}</p>
                      <p className="text-xs text-slate-500">Critical Claims</p>
                    </div>
                  </div>
                  
                  {/* Affected Frameworks */}
                  {impactAnalysis.affected_frameworks?.length > 0 && (
                    <div className="p-4 bg-slate-50 border-t border-slate-200">
                      <p className="text-xs text-slate-500 mb-2">Affected Frameworks:</p>
                      <div className="flex flex-wrap gap-2">
                        {impactAnalysis.affected_frameworks.map(fw => (
                          <span key={fw} className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                            {fw}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Affected Requirements */}
                <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="affected-requirements">
                  <button
                    onClick={() => toggleSection('requirements')}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      <Target size={18} className="text-purple-600" />
                      <h3 className="font-bold text-slate-900">Affected Requirements ({impactAnalysis.affected_requirements?.length || 0})</h3>
                    </div>
                    {expandedSections.requirements ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  
                  {expandedSections.requirements && (
                    <div className="border-t border-slate-200">
                      {impactAnalysis.affected_requirements?.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {impactAnalysis.affected_requirements.map(req => (
                            <div key={req.id} className="p-4 hover:bg-slate-50">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs text-slate-500">{req.code}</span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      req.priority === 'critical' ? 'bg-red-100 text-red-700' :
                                      req.priority === 'high' ? 'bg-amber-100 text-amber-700' :
                                      'bg-slate-100 text-slate-600'
                                    }`}>
                                      {req.priority?.toUpperCase()}
                                    </span>
                                  </div>
                                  <p className="font-medium text-slate-900 mt-1">{req.title}</p>
                                  <p className="text-sm text-slate-500 mt-1">{req.description}</p>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  req.obligation_status === 'complete' ? 'bg-green-100 text-green-700' :
                                  req.obligation_status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {req.obligation_status?.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-slate-500">
                          <Target size={24} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No requirements directly affected</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Affected Claims */}
                <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="affected-claims">
                  <button
                    onClick={() => toggleSection('claims')}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      <Shield size={18} className="text-green-600" />
                      <h3 className="font-bold text-slate-900">Affected Claims ({impactAnalysis.affected_claims?.length || 0})</h3>
                    </div>
                    {expandedSections.claims ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  
                  {expandedSections.claims && (
                    <div className="border-t border-slate-200">
                      {impactAnalysis.affected_claims?.length > 0 ? (
                        <div className="divide-y divide-slate-100">
                          {impactAnalysis.affected_claims.map(claim => (
                            <div key={claim.id} className="p-4 hover:bg-slate-50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      claim.status === 'published' ? 'bg-blue-100 text-blue-700' :
                                      claim.status === 'approved' ? 'bg-green-100 text-green-700' :
                                      claim.status === 'pending_review' ? 'bg-amber-100 text-amber-700' :
                                      'bg-slate-100 text-slate-600'
                                    }`}>
                                      {claim.status?.replace('_', ' ')}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${
                                      claim.impact_source === 'direct' ? 'bg-red-50 text-red-600' : 'bg-purple-50 text-purple-600'
                                    }`}>
                                      {claim.impact_source === 'direct' ? 'Direct Impact' : 'Via Requirement'}
                                    </span>
                                  </div>
                                  <p className="font-medium text-slate-900 mt-1">{claim.title}</p>
                                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{claim.claim_text}</p>
                                </div>
                                {(claim.status === 'published' || claim.status === 'approved') && (
                                  <AlertTriangle size={18} className="text-red-500 flex-shrink-0 ml-2" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-8 text-center text-slate-500">
                          <Shield size={24} className="mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No claims affected</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Recommendations */}
                <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="recommendations">
                  <button
                    onClick={() => toggleSection('recommendations')}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
                  >
                    <div className="flex items-center gap-2">
                      <Activity size={18} className="text-indigo-600" />
                      <h3 className="font-bold text-slate-900">Recommendations</h3>
                    </div>
                    {expandedSections.recommendations ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  
                  {expandedSections.recommendations && impactAnalysis.recommendations && (
                    <div className="border-t border-slate-200 divide-y divide-slate-100">
                      {impactAnalysis.recommendations.map((rec, idx) => {
                        const config = SEVERITY_CONFIG[rec.priority] || SEVERITY_CONFIG.low;
                        const Icon = config.icon;
                        return (
                          <div key={idx} className="p-4 flex items-start gap-3">
                            <div className={`p-2 rounded-lg ${config.bgClass}`}>
                              <Icon size={16} className={config.textClass} />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{rec.action}</p>
                              <p className="text-sm text-slate-500">{rec.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {impactAnalysis && !impactAnalysis.success && (
              <div className="bg-white rounded-xl shadow p-8 text-center">
                <XCircle size={48} className="mx-auto text-red-400 mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Analysis Failed</h3>
                <p className="text-slate-500">{impactAnalysis.error || 'Unable to analyze KPI impact'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'graph' && (
        <div className="bg-white rounded-xl shadow overflow-hidden" data-testid="impact-graph">
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <GitBranch size={18} className="text-indigo-600" />
              Dependency Graph
            </h2>
            <p className="text-sm text-slate-500">Visual representation of KPI → Claim → Requirement → Report relationships</p>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <RefreshCw className="animate-spin text-indigo-600 mx-auto" size={32} />
              <p className="mt-3 text-slate-600">Loading graph data...</p>
            </div>
          ) : graphData ? (
            <div className="p-6">
              {/* Graph Summary */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {Object.entries(NODE_TYPES).slice(0, 4).map(([type, config]) => {
                  const count = graphData.nodes?.filter(n => n.type === type).length || 0;
                  const Icon = config.icon;
                  return (
                    <div key={type} className={`p-4 rounded-lg ${config.bgClass} border ${config.borderClass}`}>
                      <div className="flex items-center gap-2">
                        <Icon size={18} className={config.textClass} />
                        <span className={`text-sm font-medium ${config.textClass}`}>{config.label}s</span>
                      </div>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{count}</p>
                    </div>
                  );
                })}
              </div>

              {/* Simple Flow Diagram */}
              <div className="border border-slate-200 rounded-lg p-6">
                <div className="flex items-start justify-between gap-8">
                  {/* KPIs Column */}
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Database size={14} className="text-blue-600" />
                      KPIs ({graphData.summary?.kpis || 0})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {graphData.nodes?.filter(n => n.type === 'kpi').slice(0, 8).map(node => (
                        <div key={node.id} className="p-2 bg-blue-50 rounded-lg text-xs">
                          <p className="font-medium text-blue-800">{node.label}</p>
                          <p className="text-blue-600">{node.code}</p>
                        </div>
                      ))}
                      {(graphData.summary?.kpis || 0) > 8 && (
                        <p className="text-xs text-slate-500 text-center">+{graphData.summary.kpis - 8} more</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center self-center">
                    <ArrowRight size={24} className="text-slate-400" />
                  </div>

                  {/* Claims Column */}
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Shield size={14} className="text-green-600" />
                      Claims ({graphData.summary?.claims || 0})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {graphData.nodes?.filter(n => n.type === 'claim').slice(0, 8).map(node => (
                        <div key={node.id} className="p-2 bg-green-50 rounded-lg text-xs">
                          <p className="font-medium text-green-800">{node.label}</p>
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            node.status === 'published' ? 'bg-blue-100 text-blue-700' :
                            node.status === 'approved' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {node.status}
                          </span>
                        </div>
                      ))}
                      {(graphData.summary?.claims || 0) > 8 && (
                        <p className="text-xs text-slate-500 text-center">+{graphData.summary.claims - 8} more</p>
                      )}
                      {(graphData.summary?.claims || 0) === 0 && (
                        <p className="text-xs text-slate-500 text-center p-4">No claims yet</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center self-center">
                    <ArrowRight size={24} className="text-slate-400" />
                  </div>

                  {/* Requirements Column */}
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <Target size={14} className="text-purple-600" />
                      Requirements ({graphData.summary?.requirements || 0})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {graphData.nodes?.filter(n => n.type === 'requirement').slice(0, 8).map(node => (
                        <div key={node.id} className="p-2 bg-purple-50 rounded-lg text-xs">
                          <p className="font-medium text-purple-800">{node.code}</p>
                          <p className="text-purple-600 truncate">{node.label}</p>
                        </div>
                      ))}
                      {(graphData.summary?.requirements || 0) > 8 && (
                        <p className="text-xs text-slate-500 text-center">+{graphData.summary.requirements - 8} more</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center self-center">
                    <ArrowRight size={24} className="text-slate-400" />
                  </div>

                  {/* Reports Column */}
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <FileText size={14} className="text-amber-600" />
                      Reports ({graphData.summary?.frameworks || 0})
                    </h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {graphData.nodes?.filter(n => n.type === 'report').map(node => (
                        <div key={node.id} className="p-2 bg-amber-50 rounded-lg text-xs">
                          <p className="font-medium text-amber-800">{node.framework_code}</p>
                          <p className="text-amber-600 truncate">{node.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Edges Summary */}
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">{graphData.summary?.total_edges || 0}</span> total connections mapped
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center">
              <GitBranch size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No graph data available</p>
            </div>
          )}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-6 text-white">
        <h3 className="text-lg font-bold mb-2">How Impact Analysis Works</h3>
        <p className="text-slate-300 text-sm">
          When you change a KPI value, the Impact Engine traces all downstream dependencies through the 
          crosswalk mappings. This helps you understand which claims need updating, which requirements 
          are affected, and ultimately which reports need regeneration. Use this before making data 
          changes to assess the full impact on your compliance posture.
        </p>
      </div>
    </div>
  );
};

export default ImpactEngine;
